import React, { ReactElement } from 'react';
import { css, cx } from '@emotion/css';
type ClassNamesArg = string | undefined | null | false;
import { Colors } from '../styles/colors';
import { ControlHeight, Motion, Radius, Shadow } from '../styles/tokens';
import { Oval } from 'react-loader-spinner';

export enum ButtonVariantEnum {
  FILLED = 'filled',
  OUTLINE = 'outline',
  TEXT = 'text',
}

export enum ButtonSizeEnum {
  LARGE = 'large',
  MEDIUM = 'medium',
}

interface IButtonProps {
  disabled?: boolean;
  type?: any;
  variant: ButtonVariantEnum;
  size: ButtonSizeEnum;
  text: string;
  startIcon?: ReactElement<any, any>;
  endIcon?: ReactElement<any, any>;
  color?: Colors;
  onClick?(): void;
  className?: ClassNamesArg;
  fullWidth?: boolean;
  gutterbottom?: boolean;
  loading?: boolean;
}

export const Button = (props: IButtonProps): JSX.Element => {
  const {
    disabled = false,
    fullWidth = false,
    gutterbottom = false,
    type = 'button',
    variant = ButtonVariantEnum.FILLED,
    size = ButtonSizeEnum.LARGE,
    text,
    startIcon,
    endIcon,
    onClick,
    color,
    className,
    loading = false,
  } = props;

  return (
    <button
      className={cx(
        buttonStyle(variant, size, disabled, color, fullWidth, gutterbottom),
        className,
      )}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
    >
      {startIcon && startIcon}
      {loading ? (
        <Oval
          ariaLabel="loading-indicator"
          color={
            variant === ButtonVariantEnum.FILLED
              ? Colors.WHITE
              : Colors.CAPRI_BLUE
          }
          secondaryColor={
            variant === ButtonVariantEnum.FILLED
              ? 'rgba(255,255,255,0.35)'
              : `${Colors.CAPRI_BLUE}55`
          }
          strokeWidth={5}
          height={size === ButtonSizeEnum.LARGE ? 26 : 22}
          width={size === ButtonSizeEnum.LARGE ? 26 : 22}
        />
      ) : (
        text
      )}
      {endIcon && endIcon}
    </button>
  );
};

const buttonStyle = (
  variant: ButtonVariantEnum,
  size: ButtonSizeEnum,
  disabled: boolean,
  color: Colors | undefined,
  fullWidth: boolean,
  gutterbottom: boolean,
) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  border: none;
  background: none;
  font-weight: bold;
  font-size: 1.125rem;
  line-height: 100%;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
  color: ${color};
  cursor: pointer;
  border-radius: ${Radius.md};
  transition:
    background-color ${Motion.fast},
    border-color ${Motion.fast},
    color ${Motion.fast},
    box-shadow ${Motion.fast},
    transform ${Motion.fast};
  -webkit-tap-highlight-color: transparent;

  > svg {
    flex: 0 0 auto;
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: scale(0.985);
  }

  ${gutterbottom &&
  css`
    margin-bottom: 1rem;
  `}

  ${fullWidth &&
  css`
    width: 100%;
  `}

  ${size === ButtonSizeEnum.LARGE &&
  css`
    height: ${ControlHeight.large};
    padding: 0 1.75rem;
  `}

  ${size === ButtonSizeEnum.MEDIUM &&
  css`
    height: ${ControlHeight.medium};
    padding: 0 1rem;
    font-size: 1rem;
  `}

  ${variant === ButtonVariantEnum.FILLED &&
  css`
    color: ${color ? color : Colors.WHITE};
    background: ${Colors.CAPRI_BLUE};
    border: 2px solid ${Colors.CAPRI_BLUE};
    box-shadow: ${Shadow.brand};

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: ${Colors.CAPRI_BLUE_DARK};
        border-color: ${Colors.CAPRI_BLUE_DARK};
      }
    }
  `}

  ${variant === ButtonVariantEnum.OUTLINE &&
  css`
    color: ${Colors.CAPRI_BLUE};
    border: 2px solid ${Colors.CAPRI_BLUE};
    background: ${Colors.WHITE};

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: ${Colors.CAPRI_BLUE_TINT};
      }
    }
  `}

  ${variant === ButtonVariantEnum.TEXT &&
  css`
    padding-left: 0px;
    padding-right: 0px;
    color: ${color ? color : Colors.CAPRI_BLUE};

    @media (hover: hover) {
      &:hover:not(:disabled) {
        color: ${color ? color : Colors.CAPRI_BLUE_DARK};
      }
    }
  `}

  ${disabled === true &&
  css`
    color: ${Colors.WINDOW_GRAY};
    cursor: not-allowed;
  `}

  ${variant === ButtonVariantEnum.OUTLINE &&
  disabled === true &&
  css`
    border-color: ${Colors.BORDER_STRONG};
  `}

  ${variant === ButtonVariantEnum.FILLED &&
  disabled === true &&
  css`
    color: ${Colors.WHITE};
    border: 2px solid ${Colors.BORDER_STRONG};
    background-color: ${Colors.BORDER_STRONG};
    box-shadow: none;
  `}
`;
